import * as React from "react";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";

/**
 * Ordered series palette, all derived from design-system tokens so the chart
 * re-themes automatically in dark mode (the CSS variables are redefined under
 * the dark theme). `color-mix` rotates the accent hue to keep series visually
 * distinct without introducing ad-hoc hex values.
 */
export const CHART_SERIES_COLORS = [
  "var(--color-accent)",
  "color-mix(in oklab, var(--color-accent), #f59e0b 55%)",
  "color-mix(in oklab, var(--color-accent), #ec4899 55%)",
  "color-mix(in oklab, var(--color-accent), #22c55e 55%)",
  "color-mix(in oklab, var(--color-accent), #8b5cf6 55%)",
  "color-mix(in oklab, var(--color-accent), #ef4444 50%)",
  "color-mix(in oklab, var(--color-accent), #14b8a6 55%)",
  "color-mix(in oklab, var(--color-accent), #eab308 60%)",
] as const;

export function seriesColor(index: number): string {
  return CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length];
}

export interface ChartSeries {
  /** Data key present on every row object. */
  key: string;
  /** Human label shown in the legend and tooltip. */
  name: string;
}

export interface TimeseriesChartProps {
  /** One object per x-axis point. Each series key maps to a numeric value. */
  data: Array<Record<string, number | string | null>>;
  /** Series to plot, in render/legend order. */
  series: ChartSeries[];
  /** Row key for the x-axis category (default `"label"`). */
  xKey?: string;
  /** Formats y-axis ticks and tooltip values (e.g. compact IDR). */
  valueFormatter?: (value: number) => string;
  /** Pixel height of the chart area (default 360). */
  height?: number;
  /** Message shown when there is no data to plot. */
  emptyMessage?: string;
  /**
   * Optional horizontal reference line (e.g. a threshold the series is measured
   * against). Drawn dashed in the danger token. `null`/`undefined` hides it.
   */
  referenceValue?: number | null;
  /** Label rendered alongside the reference line. */
  referenceLabel?: string;
  className?: string;
}

/**
 * SSR-safe multi-series line chart, themed with design-system tokens.
 *
 * Recharts' ResponsiveContainer measures the DOM (touches `window`), so we gate
 * it behind a post-hydration `mounted` flag. Before mount — including during
 * server-side rendering — we emit a fixed-height placeholder so the markup is
 * stable and hydration matches; the chart paints on the client after mount.
 */
export function TimeseriesChart({
  data,
  series,
  xKey = "label",
  valueFormatter,
  height = 360,
  emptyMessage = "Tidak ada data untuk filter ini.",
  referenceValue,
  referenceLabel,
  className,
}: TimeseriesChartProps) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const isEmpty = data.length === 0 || series.length === 0;

  if (isEmpty) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed border-hairline text-sm text-ink-muted",
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  if (!mounted) {
    return (
      <div
        className={cn("animate-pulse rounded-lg bg-surface", className)}
        style={{ height }}
        aria-hidden="true"
      />
    );
  }

  const tickFmt = (v: number) =>
    valueFormatter ? valueFormatter(v) : String(v);

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-hairline)"
            vertical={false}
          />
          <XAxis
            dataKey={xKey}
            tick={{ fill: "var(--color-ink-muted)", fontSize: 12 }}
            stroke="var(--color-hairline)"
            tickMargin={8}
          />
          <YAxis
            tickFormatter={tickFmt}
            tick={{ fill: "var(--color-ink-muted)", fontSize: 12 }}
            stroke="var(--color-hairline)"
            width={64}
          />
          <Tooltip
            formatter={(value, name) => [
              valueFormatter && typeof value === "number"
                ? valueFormatter(value)
                : (value as React.ReactNode),
              name,
            ]}
            contentStyle={{
              backgroundColor: "var(--color-surface)",
              border: "1px solid var(--color-hairline)",
              borderRadius: "0.5rem",
              color: "var(--color-ink-body)",
              fontSize: 12,
            }}
            labelStyle={{ color: "var(--color-ink-display)", fontWeight: 600 }}
            itemStyle={{ color: "var(--color-ink-body)" }}
            cursor={{ stroke: "var(--color-ink-muted)", strokeWidth: 1 }}
          />
          <Legend
            wrapperStyle={{ fontSize: 12, color: "var(--color-ink-body)" }}
          />
          {referenceValue != null && (
            <ReferenceLine
              y={referenceValue}
              stroke="var(--color-danger)"
              strokeDasharray="6 4"
              strokeWidth={1.5}
              ifOverflow="extendDomain"
              label={
                referenceLabel
                  ? {
                      value: referenceLabel,
                      position: "insideTopRight",
                      fill: "var(--color-danger)",
                      fontSize: 11,
                    }
                  : undefined
              }
            />
          )}
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={seriesColor(i)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface CompositionSlice {
  /** Human label for the slice (e.g. a channel or region name). */
  label: string;
  /** Absolute value of the slice. */
  value: number;
  /** Fraction of the whole (0..1). Optional; derived if absent. */
  share?: number;
}

export interface CompositionChartProps {
  /** Slices, in render order (typically value desc). */
  data: CompositionSlice[];
  /** Formats the center total and tooltip/legend values (e.g. compact IDR). */
  valueFormatter?: (value: number) => string;
  /** Pixel height of the donut area (default 240). */
  height?: number;
  /** Message shown when there is nothing to plot. */
  emptyMessage?: string;
  className?: string;
}

/**
 * SSR-safe donut for parts-of-whole composition (Share of Wallet), themed with
 * design-system tokens. Same post-hydration `mounted` gate as TimeseriesChart:
 * ResponsiveContainer measures the DOM, so before mount (incl. SSR) we render a
 * stable fixed-height placeholder, then paint after hydration.
 *
 * A center label shows the total; a share legend lists each slice with its
 * percentage and value. Slice colors come from the shared token palette.
 */
export function CompositionChart({
  data,
  valueFormatter,
  height = 240,
  emptyMessage = "Tidak ada data untuk filter ini.",
  className,
}: CompositionChartProps) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const total = data.reduce((sum, s) => sum + s.value, 0);
  const isEmpty = data.length === 0 || total <= 0;

  const fmtValue = (v: number) => (valueFormatter ? valueFormatter(v) : String(v));
  const fmtShare = (s: number) =>
    new Intl.NumberFormat("id-ID", {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(s);

  if (isEmpty) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed border-hairline text-sm text-ink-muted",
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  if (!mounted) {
    return (
      <div
        className={cn("animate-pulse rounded-lg bg-surface", className)}
        style={{ height }}
        aria-hidden="true"
      />
    );
  }

  return (
    <div className={cn("flex flex-col items-center gap-4 sm:flex-row", className)}>
      <div className="relative w-full sm:w-1/2" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="85%"
              paddingAngle={1}
              stroke="var(--color-surface)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={seriesColor(i)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [
                typeof value === "number" ? fmtValue(value) : (value as React.ReactNode),
                name,
              ]}
              contentStyle={{
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-hairline)",
                borderRadius: "0.5rem",
                color: "var(--color-ink-body)",
                fontSize: 12,
              }}
              itemStyle={{ color: "var(--color-ink-body)" }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs text-ink-muted">Total</span>
          <span className="text-sm font-semibold text-ink-display">{fmtValue(total)}</span>
        </div>
      </div>

      <ul className="w-full space-y-1.5 sm:w-1/2">
        {data.map((slice, i) => (
          <li key={slice.label} className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: seriesColor(i) }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-ink-body">{slice.label}</span>
            <span className="tabular-nums font-medium text-ink-display">
              {fmtShare(slice.share ?? slice.value / total)}
            </span>
            <span className="tabular-nums text-xs text-ink-muted">{fmtValue(slice.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
